import { useCallback, useMemo } from 'react';
import Select from '@workpay/ds-shared/components/ui/Select';
import type { SelectOption } from '@workpay/ds-shared/components/ui/Select';
import { useTranslation } from '@hooks/useTranslation';
import type { FormAccessLevel } from '@/types/form';
import type { FormTemplateFormEntry } from '@/types/formTemplate';
import styles from './EmployeeFormSettingsPanel.module.scss';

// ============================================
// TYPES
// ============================================

interface EmployeeFormSettingsPanelProps {
  entry: FormTemplateFormEntry;
  onChange: (updated: FormTemplateFormEntry) => void;
}

// ============================================
// COMPONENT
// ============================================

function EmployeeFormSettingsPanel({
  entry,
  onChange,
}: EmployeeFormSettingsPanelProps) {
  const { t } = useTranslation();

  const ACCESS_LEVEL_OPTIONS: SelectOption[] = useMemo(() => [
    { label: t('K_ACCESS_READ_WRITE', 'Read & Write'), value: 'READ_WRITE' },
    { label: t('K_ACCESS_READ_ONLY', 'Read Only'), value: 'READ' },
    { label: t('K_ACCESS_NO_ACCESS', 'No Access'), value: 'NO_ACCESS' },
  ], [t]);
  // accessOverrides.EMPLOYEE = what EMPLOYEE sees (only when different from accessLevel)
  const employeeAccess = entry.accessOverrides?.EMPLOYEE ?? entry.accessLevel;

  // accessLevel = what TENANT sees (also the default for any non-WORKPAY caller)
  const handleClientAccessChange = useCallback(
    (value: string) => {
      const newClientLevel = value as FormAccessLevel;
      const currentEmployeeAccess = entry.accessOverrides?.EMPLOYEE ?? entry.accessLevel;
      const overrides = { ...(entry.accessOverrides ?? {}) };

      // Pin current employee access as explicit override if it would change due to fallback
      if (currentEmployeeAccess !== newClientLevel) {
        overrides.EMPLOYEE = currentEmployeeAccess;
      } else {
        // Employee matches new client — override is redundant
        delete overrides.EMPLOYEE;
      }

      const finalOverrides = Object.keys(overrides).length > 0 ? overrides : undefined;

      console.log('[AccessLevel] Client access changed:', {
        previous: entry.accessLevel,
        new: newClientLevel,
        employeeAccessKept: currentEmployeeAccess,
        finalOverrides,
      });

      onChange({
        ...entry,
        accessLevel: newClientLevel,
        accessOverrides: finalOverrides,
      });
    },
    [entry, onChange],
  );

  const handleEmployeeAccessChange = useCallback(
    (value: string) => {
      const newLevel = value as FormAccessLevel;
      const overrides = { ...(entry.accessOverrides ?? {}) };

      // If same as accessLevel, remove the override (not needed)
      if (newLevel === entry.accessLevel) {
        delete overrides.EMPLOYEE;
      } else {
        overrides.EMPLOYEE = newLevel;
      }

      const finalOverrides = Object.keys(overrides).length > 0 ? overrides : undefined;

      console.log('[AccessLevel] Employee access changed:', {
        previous: entry.accessOverrides?.EMPLOYEE ?? entry.accessLevel,
        new: newLevel,
        clientAccess: entry.accessLevel,
        overrideNeeded: newLevel !== entry.accessLevel,
        finalOverrides,
      });

      onChange({
        ...entry,
        accessOverrides: finalOverrides,
      });
    },
    [entry, onChange],
  );

  const handleIsDefaultChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...entry,
        attributes: { ...entry.attributes, isDefault: e.target.checked },
      });
    },
    [entry, onChange],
  );

  const handleIsMandatoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...entry,
        attributes: { ...entry.attributes, isMandatory: e.target.checked },
      });
    },
    [entry, onChange],
  );

  return (
    <div className={styles.grid}>
      {/* Client / Tenant access — accessLevel */}
      <div>
        <Select
          label={t('K_LABEL_CLIENT_ACCESS', 'Client access')}
          options={ACCESS_LEVEL_OPTIONS}
          value={entry.accessLevel}
          onChange={handleClientAccessChange}
          fullWidth
        />
      </div>

      {/* Employee access — accessOverrides.EMPLOYEE */}
      <div>
        <Select
          label={t('K_LABEL_EMPLOYEE_ACCESS', 'Employee access')}
          options={ACCESS_LEVEL_OPTIONS}
          value={employeeAccess}
          onChange={handleEmployeeAccessChange}
          fullWidth
        />
      </div>

      {/* Default Form + Mandatory Form — Checkboxes */}
      <div className={styles.defaultFormCell}>
        <label className={styles.defaultFormLabel}>
          <input
            type="checkbox"
            checked={entry.attributes.isDefault}
            onChange={handleIsDefaultChange}
            className={styles.defaultFormCheckbox}
          />
          <span>{t('K_EMPLOYEE_FORM', 'Employee form')}</span>
        </label>
        <label className={styles.defaultFormLabel}>
          <input
            type="checkbox"
            checked={entry.attributes.isMandatory ?? false}
            onChange={handleIsMandatoryChange}
            className={styles.defaultFormCheckbox}
          />
          <span>{t('K_LABEL_MANDATORY_FORM_EMPLOYEE', 'Mandatory form while creating employee')}</span>
        </label>
      </div>
    </div>
  );
}

export default EmployeeFormSettingsPanel;
